import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Upload, Search, BookOpen, ShieldCheck, MessageCircle, MapPin, Clock, Stethoscope, HeartPulse, ArrowRight, Target } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function HomePage() {
  const { user } = useAuth();

  return (
    <div className="bg-slate-50">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-60"></div>
        <div className="relative max-w-6xl mx-auto px-4 pt-10 pb-8 sm:pt-16 sm:pb-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium" data-testid="hero-tag">
              <ShieldCheck className="w-3.5 h-3.5" />
              Conectamos con orden, claridad y confianza
            </span>
            <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-blue-950">
              Encuentra el <span className="text-blue-600">medicamento</span> que necesitas, o ayuda a otra familia.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Publica medicamentos en buen estado que ya no usas, o
              encuentra el que tu familia necesita a precios accesibles.
              Sin grupos confusos. Aquí hay fotos, caducidad y ubicación.
            </p>

            <div className="mt-7 grid sm:grid-cols-3 gap-3 max-w-xl">
              <Link to={user ? "/ofrecer" : "/login"} data-testid="hero-cta-publish">
                <Button className="h-14 w-full text-base bg-blue-600 hover:bg-blue-700 text-white rounded-xl btn-press shadow-md">
                  <Upload className="w-5 h-5 mr-2" /> Ofrecer
                </Button>
              </Link>
              <Link to="/medicamentos" data-testid="hero-cta-browse">
                <Button variant="outline" className="h-14 w-full text-base border-2 border-blue-200 text-blue-700 hover:bg-blue-50 rounded-xl btn-press">
                  <Search className="w-5 h-5 mr-2" /> Buscar
                </Button>
              </Link>
              <Link to="/busquedas" data-testid="hero-cta-requests">
                <Button variant="outline" className="h-14 w-full text-base border-2 border-amber-200 text-amber-800 hover:bg-amber-50 rounded-xl btn-press">
                  <Target className="w-5 h-5 mr-2" /> Tablero
                </Button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden border-4 border-white shadow-xl bg-blue-100">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/3e27f2b2-ddd5-4dc1-b8b7-493e36ca658e/images/5f74ca6dae929490a605bef6fda79dfc4cf31c88027ed7078cf395ae2275b5b0.png"
                alt="Enfermera entregando medicamento"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1691139601099-932c01ec198b"; }}
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white shadow-lg rounded-2xl px-4 py-3 border border-slate-200 hidden sm:flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_conecta-medicinas/artifacts/padqrbhb_Logo_CE_fondoBlanco.png"
                alt="Ciudad Enfermería"
                className="h-12 w-12 rounded-full object-contain"
              />
              <div>
                <div className="text-sm font-semibold text-slate-900">Con respaldo de</div>
                <div className="text-xs text-slate-500">Ciudad Enfermería SLP</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three sections explainer */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">¿Cómo funciona?</h2>
        <p className="text-slate-600 mt-2 text-base">Tres pasos sencillos. Hecho para todos.</p>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[
            { icon: Upload, color: "bg-blue-600", title: "1. Publica", body: "Sube 4 fotos del medicamento, indica cuántas piezas tienes, precio, zona y tu WhatsApp." },
            { icon: Search, color: "bg-blue-500", title: "2. Encuentra", body: "Busca por categoría o zona de la ciudad. Mira fotos reales, caducidad y precio claro." },
            { icon: MessageCircle, color: "bg-emerald-500", title: "3. Contacta", body: "Habla directamente por WhatsApp con quien ofrece. Tú decides cómo y cuándo concretar." },
          ].map((s, i) => (
            <Card key={i} className="p-6 rounded-2xl border border-slate-200 bg-white" data-testid={`step-card-${i}`}>
              <div className={`h-12 w-12 rounded-xl ${s.color} text-white grid place-items-center shadow-md`}>
                <s.icon className="w-6 h-6" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Nursing service CTA */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <Card className="rounded-3xl overflow-hidden border border-blue-200 bg-gradient-to-br from-blue-600 to-blue-800 text-white p-6 sm:p-10 lg:p-12 grid lg:grid-cols-5 gap-6 items-center shadow-xl" data-testid="home-service-cta">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-blue-50 text-xs font-medium backdrop-blur">
              <Stethoscope className="w-3.5 h-3.5" /> Nuestro servicio principal
            </div>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              ¿Tu familiar necesita cuidado profesional en casa?
            </h2>
            <p className="mt-3 text-blue-100 text-base leading-relaxed max-w-xl">
              Ciudad Enfermería ofrece enfermeras tituladas para aplicación de medicamentos,
              curaciones, cuidado de adulto mayor y postoperatorio. Solicítalo en un minuto.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link to="/servicios" data-testid="home-service-cta-btn">
                <Button className="h-14 px-6 text-base bg-white text-blue-700 hover:bg-blue-50 rounded-xl btn-press shadow-md">
                  Solicitar servicio <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-2 hidden sm:grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/10 backdrop-blur p-4 border border-white/15">
              <HeartPulse className="w-7 h-7 mb-2 text-blue-100" />
              <div className="font-display font-semibold">Adulto mayor</div>
              <div className="text-xs text-blue-100 mt-0.5">Acompañamiento y cuidado</div>
            </div>
            <div className="rounded-2xl bg-white/10 backdrop-blur p-4 border border-white/15">
              <Stethoscope className="w-7 h-7 mb-2 text-blue-100" />
              <div className="font-display font-semibold">Postoperatorio</div>
              <div className="text-xs text-blue-100 mt-0.5">Curaciones a domicilio</div>
            </div>
            <div className="rounded-2xl bg-white/10 backdrop-blur p-4 border border-white/15">
              <Upload className="w-7 h-7 mb-2 text-blue-100" />
              <div className="font-display font-semibold">Aplicación</div>
              <div className="text-xs text-blue-100 mt-0.5">Inyecciones y sueros</div>
            </div>
            <div className="rounded-2xl bg-white/10 backdrop-blur p-4 border border-white/15">
              <ShieldCheck className="w-7 h-7 mb-2 text-blue-100" />
              <div className="font-display font-semibold">Certificadas</div>
              <div className="text-xs text-blue-100 mt-0.5">Personal titulado</div>
            </div>
          </div>
        </Card>
      </section>

      {/* Trust */}
      <section className="bg-blue-50 border-y border-blue-100">
        <div className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-blue-950">
              Un espacio con reglas claras
            </h2>
            <p className="mt-3 text-slate-700 text-base leading-relaxed">
              Ciudad Enfermería no almacena ni vende medicamentos. Somos una
              empresa de servicios de enfermería que decidió crear un lugar
              ordenado para que las familias puedan compartir e intercambiar
              medicamentos con información verificable.
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
              <div className="flex items-start gap-2"><ShieldCheck className="w-5 h-5 text-blue-600 mt-0.5"/><span><strong>Fotos obligatorias</strong><br/><span className="text-slate-600">Caja, contenido y caducidad</span></span></div>
              <div className="flex items-start gap-2"><MapPin className="w-5 h-5 text-blue-600 mt-0.5"/><span><strong>Zona indicada</strong><br/><span className="text-slate-600">Y costo de entrega claro</span></span></div>
              <div className="flex items-start gap-2"><Clock className="w-5 h-5 text-blue-600 mt-0.5"/><span><strong>Caducidad visible</strong><br/><span className="text-slate-600">Verifica antes de comprar</span></span></div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="rounded-3xl overflow-hidden border-4 border-white shadow-md aspect-[4/3]">
              <img src="https://customer-assets.emergentagent.com/job_conecta-medicinas/artifacts/v7o2lbes_Gemini_Generated_Image_%20%282%29.png" alt="Enfermera atendiendo a un paciente" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
